var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, useContext, useMemo, useEffect, useState } from 'react';
import LoadingAnimation from '../base/LoadingAnimation';
import SignInActions, { getUserSettings } from '../base/SignInActions';
import { userSettingsSelectFunction } from '../data/hooks/currentUser';
import { usersApi } from '../data/rest/api';
import { handleApiErrorMessage } from '../util/message';
var AuthContext = createContext({ isSigningIn: true });
export function AuthContextProvider(_a) {
    var ignore = _a.ignore, children = _a.children;
    var _b = __read(useState(true), 2), isSigningIn = _b[0], setIsSigningIn = _b[1];
    var queryClient = useQueryClient();
    var user = useQuery(['currentUser'], function () { return usersApi.getCurrent(); }, {
        staleTime: 5000,
        notifyOnChangeProps: ['data'],
        enabled: !isSigningIn,
    }).data;
    useQuery;
    var settings = useQuery(['currentUserSettings'], function () { return usersApi.getSmartSettings(); }, {
        staleTime: Infinity,
        notifyOnChangeProps: ['data'],
        enabled: !isSigningIn,
    }).data;
    useEffect(function () {
        var isMounted = true;
        function signIn() {
            return __awaiter(this, void 0, void 0, function () {
                var current_1, error_1;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            _a.trys.push([0, 4, 5, 6]);
                            return [4 /*yield*/, SignInActions.signIn()];
                        case 1:
                            _a.sent();
                            return [4 /*yield*/, usersApi.getCurrent()];
                        case 2:
                            current_1 = _a.sent();
                            return [4 /*yield*/, getUserSettings(queryClient)];
                        case 3:
                            _a.sent();
                            //@ts-ignore
                            zE === null || zE === void 0 ? void 0 : zE('messenger', 'loginUser', function (callback) {
                                callback(current_1.zendeskJwt);
                            });
                            return [3 /*break*/, 6];
                        case 4:
                            error_1 = _a.sent();
                            handleApiErrorMessage(error_1);
                            return [3 /*break*/, 6];
                        case 5:
                            if (isMounted) {
                                setIsSigningIn(false);
                            }
                            return [7 /*endfinally*/];
                        case 6: return [2 /*return*/];
                    }
                });
            });
        }
        if (!ignore) {
            signIn();
        }
        return function () {
            isMounted = false;
        };
    }, [ignore]);
    var context = useMemo(function () { return ({
        isSigningIn: isSigningIn,
        user: user,
        settings: userSettingsSelectFunction(settings),
    }); }, [user, settings]);
    return (React.createElement(AuthContext.Provider, { value: context }, isSigningIn && !ignore ? React.createElement(LoadingAnimation, { message: "Signing in..." }) : children));
}
export function useAuth() {
    return useContext(AuthContext);
}
